import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar, faComments, faUsers, faClipboardList,
  faClock, faFileAlt, faVideo, faChartLine,
  faExclamationTriangle, faEnvelope, faCheckCircle,
  faTimesCircle, faSyncAlt, faPlus,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../../../app/Layout';
import HeaderContent from '../../General/HeaderContent';
import Breadcrumb from '../../General/Breadcrumb';
import SpinnerLoading from '../../../components/Spinners/SpinnerLoading';
import {
  obtenerKpisTutoria,
  obtenerDim3,
  registrarDim3,
  responderDim3,
} from '../../../../actions/tutoriaHU9Actions';

const semaforo = (valor, meta) => {
  if (meta === undefined) return '#17a2b8';
  const pct = meta > 0 ? (valor / meta) * 100 : 100;
  if (valor >= meta)      return '#28a745';
  if (pct >= 80)          return '#fd7e14';
  return '#dc3545';
};

const KpiCard = ({ titulo, valor, meta, subtitulo, color, icon }) => {
  const numVal = parseFloat(valor) || 0;
  const color_real = semaforo(numVal, meta);
  const cumple = meta !== undefined && numVal >= meta;
  const barPct = meta !== undefined ? Math.min(100, Math.round((numVal / meta) * 100)) : numVal;

  return (
    <div className="col-md-3 col-sm-6 mb-3">
      <div className="box p-3 h-100" style={{ borderTop: `4px solid ${color_real}` }}>
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div
            style={{
              width: 40, height: 40, borderRadius: 8,
              backgroundColor: `${color_real}20`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FontAwesomeIcon icon={icon} style={{ color: color_real, fontSize: 18 }} />
          </div>
          {meta !== undefined && (
            <FontAwesomeIcon
              icon={cumple ? faCheckCircle : faTimesCircle}
              style={{ color: cumple ? '#28a745' : '#dc3545', fontSize: 18 }}
            />
          )}
        </div>

        <h2 style={{ color: color_real, fontWeight: 700, fontSize: 34, marginBottom: 2 }}>{valor}</h2>
        <p className="mb-1" style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{titulo}</p>

        {meta !== undefined && (
          <>
            <div className="progress mb-1" style={{ height: 6, borderRadius: 3 }}>
              <div
                className="progress-bar"
                style={{
                  width: `${barPct}%`,
                  backgroundColor: color_real,
                  borderRadius: 3,
                  transition: 'width .5s ease',
                }}
              />
            </div>
            <small style={{ color: color_real, fontSize: 11 }}>
              Meta: {meta}% — {cumple ? 'Cumplida' : `Falta ${meta - numVal}%`}
            </small>
          </>
        )}

        {subtitulo && (
          <small className="text-muted d-block mt-1" style={{ fontSize: 11 }}>{subtitulo}</small>
        )}
      </div>
    </div>
  );
};

const DimHeader = ({ numero, titulo, color }) => (
  <div className="d-flex align-items-center mb-2 mt-3" style={{ borderLeft: `4px solid ${color}`, paddingLeft: 10 }}>
    <span
      style={{
        background: color, color: '#fff', borderRadius: 4,
        fontSize: 11, fontWeight: 700, padding: '2px 8px', marginRight: 8,
      }}
    >
      DIM {numero}
    </span>
    <small className="text-uppercase font-weight-bold text-muted">{titulo}</small>
  </div>
);

const EVAL_COLOR = { excelente: 'success', buena: 'primary', regular: 'warning', deficiente: 'danger' };

const TIPO_LABELS = {
  bajo_avance: { label: 'Bajo avance', color: '#fd7e14', plazo: '48h' },
  buzon:       { label: 'Buzón',        color: '#6f42c1', plazo: '24h' },
};

const FormDim3 = ({ idUsuario, onRegistrado }) => {
  const dispatch = useDispatch();
  const [tipo,        setTipo]        = useState('bajo_avance');
  const [fechaEvento, setFechaEvento] = useState('');
  const [observacion, setObservacion] = useState('');
  const [guardando,   setGuardando]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fechaEvento) return;
    setGuardando(true);
    await dispatch(registrarDim3({ id_usuario: idUsuario, tipo, fecha_evento: fechaEvento, observacion }));
    setFechaEvento('');
    setObservacion('');
    setGuardando(false);
    if (onRegistrado) onRegistrado();
  };

  return (
    <form onSubmit={handleSubmit} className="form-inline flex-wrap" style={{ gap: 8 }}>
      <select
        className="form-control form-control-sm"
        value={tipo}
        onChange={e => setTipo(e.target.value)}
        style={{ minWidth: 140 }}
      >
        <option value="bajo_avance">Bajo avance (48h)</option>
        <option value="buzon">Buzón (24h)</option>
      </select>
      <input
        type="datetime-local"
        className="form-control form-control-sm"
        value={fechaEvento}
        onChange={e => setFechaEvento(e.target.value)}
        required
      />
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="Observación (opcional)"
        value={observacion}
        onChange={e => setObservacion(e.target.value)}
        style={{ minWidth: 200 }}
      />
      <button type="submit" className="btn btn-sm btn-primary" disabled={guardando}>
        <FontAwesomeIcon icon={faPlus} className="mr-1" />
        {guardando ? 'Registrando...' : 'Registrar caso'}
      </button>
    </form>
  );
};

const MESES = [
  { v: 1, l: 'Enero' }, { v: 2, l: 'Febrero' }, { v: 3, l: 'Marzo' },
  { v: 4, l: 'Abril' }, { v: 5, l: 'Mayo' },    { v: 6, l: 'Junio' },
  { v: 7, l: 'Julio' }, { v: 8, l: 'Agosto' },  { v: 9, l: 'Setiembre' },
  { v: 10, l: 'Octubre' }, { v: 11, l: 'Noviembre' }, { v: 12, l: 'Diciembre' },
];
const anioActual = new Date().getFullYear();
const ANIOS = [anioActual, anioActual - 1, anioActual - 2];

const KpisTutoria = () => {
  const dispatch = useDispatch();
  const { usuario }                               = useSelector(s => s.usuario);
  const { kpis, loadingKpis, dim3, loadingDim3 }  = useSelector(s => s.tutoriaHU9);

  const [filtroMes,  setFiltroMes]  = useState(new Date().getMonth() + 1);
  const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear());
  const [modalEval,  setModalEval]  = useState(false);

  useEffect(() => {
    if (usuario.id_usuario) {
      dispatch(obtenerKpisTutoria(usuario.id_usuario, filtroMes || null, filtroAnio || null));
      dispatch(obtenerDim3(usuario.id_usuario));
    }
  }, [usuario.id_usuario]);

  const handleFiltrar = () => {
    dispatch(obtenerKpisTutoria(usuario.id_usuario, filtroMes || null, filtroAnio || null));
  };

  const handleLimpiar = () => {
    setFiltroMes('');
    setFiltroAnio('');
    dispatch(obtenerKpisTutoria(usuario.id_usuario, null, null));
  };

  const handleRefrescar = () => {
    dispatch(obtenerKpisTutoria(usuario.id_usuario, filtroMes || null, filtroAnio || null));
  };

  if (loadingKpis) return <Layout><SpinnerLoading loading={true} /></Layout>;

  const data = kpis || {};

  const pctAsist = data.promedio_inscritos > 0
    ? Math.round((data.promedio_conectados / data.promedio_inscritos) * 100)
    : 0;

  const pendientes = (dim3 || []).filter(c => !c.fecha_accion);
  const resueltos  = (dim3 || []).filter(c =>  c.fecha_accion);

  return (
    <Layout>
      <HeaderContent title="KPIs de Tutoría" icon="chart-bar" />
      <Breadcrumb link={[
        { url: '/inicio', nombre: 'Inicio', icon: 'fas fa-home' },
        { url: '',        nombre: 'KPIs de Tutoría' },
      ]} />

      {/* Filtro */}
      <div className="box p-3 mb-3">
        <div className="d-flex flex-wrap align-items-end" style={{ gap: 12 }}>
          <div>
            <label className="mb-1 d-block"><small><strong>Mes</strong></small></label>
            <select
              className="form-control form-control-sm"
              value={filtroMes}
              onChange={e => setFiltroMes(e.target.value)}
              style={{ minWidth: 130 }}
            >
              <option value="">Todos los meses</option>
              {MESES.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 d-block"><small><strong>Año</strong></small></label>
            <select
              className="form-control form-control-sm"
              value={filtroAnio}
              onChange={e => setFiltroAnio(e.target.value)}
              style={{ minWidth: 100 }}
            >
              <option value="">Todos los años</option>
              {ANIOS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleFiltrar}>Filtrar</button>
          {(filtroMes || filtroAnio) && (
            <button className="btn btn-outline-secondary btn-sm" onClick={handleLimpiar}>Limpiar</button>
          )}
          <button className="btn btn-outline-secondary btn-sm ml-auto" onClick={handleRefrescar} title="Actualizar KPIs">
            <FontAwesomeIcon icon={faSyncAlt} />
          </button>
        </div>
        {(filtroMes || filtroAnio) && (
          <small className="text-muted d-block mt-2">
            Mostrando: {filtroMes ? MESES.find(m => String(m.v) === String(filtroMes))?.l : 'Todos los meses'}
            {filtroAnio ? ` ${filtroAnio}` : ''}
          </small>
        )}
      </div>

      {/* Dimensión 1 */}
      <DimHeader numero="1" titulo="Experiencia del Alumno" color="#007bff" />
      <div className="row">
        <KpiCard icon={faStar}     titulo="Satisfacción con la sesión" valor={`${data.pct_satisfaccion ?? 0}%`}      meta={90}  subtitulo="alumnos que califican ≥ 4/5 al tutor"  color="#007bff" />
        <KpiCard icon={faComments} titulo="Chat respondido al 100%"    valor={`${data.pct_chat_respondido ?? 0}%`}   meta={100} subtitulo="sesiones con chat respondido al 100%"   color="#e83e8c" />
        <KpiCard icon={faUsers}    titulo="Asistencia promedio"         valor={`${pctAsist}%`}                                   subtitulo={`${data.promedio_conectados ?? 0} conectados / ${data.promedio_inscritos ?? 0} inscritos`} color="#28a745" />
      </div>

      {/* Dimensión 2 */}
      <DimHeader numero="2" titulo="Cumplimiento Operativo" color="#6f42c1" />
      <div className="row">
        <KpiCard icon={faClipboardList} titulo="Protocolo completado"  valor={`${data.protocolo_pct ?? 0}%`}          meta={95}  subtitulo="sesiones con protocolo completo"    color="#6f42c1" />
        <KpiCard icon={faClock}         titulo="Sala abierta a tiempo" valor={`${data.pct_sala_tiempo ?? 0}%`}         meta={100} subtitulo="abiertas antes de 6:50 PM"          color="#17a2b8" />
        <KpiCard icon={faFileAlt}       titulo="Reportes a tiempo"     valor={`${data.pct_enviados_tiempo ?? 0}%`}     meta={100} subtitulo="enviados antes de 10:25 PM"         color="#fd7e14" />
        <KpiCard icon={faVideo}         titulo="Grabación a Vimeo"     valor={`${data.pct_grabacion_vimeo ?? 0}%`}     meta={100} subtitulo="grabaciones confirmadas"          color="#20c997" />
      </div>

      {/* Dimensión 3 */}
      <DimHeader numero="3" titulo="Seguimiento Activo" color="#28a745" />
      <div className="row">
        <KpiCard icon={faChartLine}         titulo="Sesiones con ≥ 80% asistencia" valor={`${data.pct_asistencia_80 ?? 0}%`}   meta={80} subtitulo="calculado de los reportes enviados" color="#28a745" />
        <KpiCard icon={faExclamationTriangle} titulo="Bajo avance contactados"      valor={`${data.pct_bajo_avance_48h ?? 0}%`} meta={90} subtitulo="contactados en ≤ 48h"               color="#fd7e14" />
        <KpiCard icon={faEnvelope}            titulo="Buzón respondido"             valor={`${data.pct_buzon_24h ?? 0}%`}       meta={90} subtitulo="respondidas en ≤ 24h"                color="#6f42c1" />
      </div>

      {/* Panel Dim3 */}
      <div className="box p-3 mt-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-success" />
            <strong>Registro de seguimiento — Dimensión 3</strong>
          </h6>
        </div>
        <div className="mb-3">
          <small className="text-muted d-block mb-2">
            Registra cuándo detectas un alumno con bajo avance o recibes una consulta de buzón. Luego márcalo como atendido para calcular el KPI.
          </small>
          <FormDim3 idUsuario={usuario.id_usuario} onRegistrado={handleRefrescar} />
        </div>

        {loadingDim3 ? (
          <p className="text-muted">Cargando...</p>
        ) : (
          <>
            {pendientes.length > 0 && (
              <div className="mb-3">
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon icon={faTimesCircle} className="text-danger mr-2" />
                  <strong className="text-danger">Pendientes ({pendientes.length})</strong>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th>Tipo</th>
                        <th>Detectado</th>
                        <th>Observación</th>
                        <th>Plazo</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendientes.map(c => {
                        const info = TIPO_LABELS[c.tipo] || {};
                        const limite = c.tipo === 'bajo_avance' ? 48 : 24;
                        const horasTranscurridas = (Date.now() - new Date(c.fecha_evento).getTime()) / 3600000;
                        const vencido = horasTranscurridas > limite;
                        return (
                          <tr key={c.id_seguimiento} className={vencido ? 'table-danger' : ''}>
                            <td>
                              <Badge variant="secondary" style={{ backgroundColor: info.color }}>
                                {info.label}
                              </Badge>
                            </td>
                            <td><small>{c.fecha_evento}</small></td>
                            <td><small>{c.observacion || '—'}</small></td>
                            <td>
                              <small className={vencido ? 'text-danger font-weight-bold' : 'text-success'}>
                                <FontAwesomeIcon icon={faClock} className="mr-1" />
                                {vencido
                                  ? `Vencido (${Math.round(horasTranscurridas)}h)`
                                  : `${Math.round(horasTranscurridas)}h / ${info.plazo}`
                                }
                              </small>
                            </td>
                            <td>
                              <button
                                className="btn btn-xs btn-success"
                                onClick={() => dispatch(responderDim3(c.id_seguimiento))}
                                style={{ fontSize: 11, padding: '2px 8px' }}
                              >
                                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                Atendido
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {resueltos.length > 0 && (
              <div>
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-success mr-2" />
                  <strong className="text-muted">Atendidos recientes ({resueltos.length})</strong>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th>Tipo</th>
                        <th>Detectado</th>
                        <th>Atendido</th>
                        <th>Resultado</th>
                        <th>Observación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resueltos.map(c => {
                        const info = TIPO_LABELS[c.tipo] || {};
                        return (
                          <tr key={c.id_seguimiento}>
                            <td>
                              <Badge variant="secondary" style={{ backgroundColor: info.color }}>
                                {info.label}
                              </Badge>
                            </td>
                            <td><small>{c.fecha_evento}</small></td>
                            <td><small>{c.fecha_accion}</small></td>
                            <td>
                              {parseInt(c.resuelto_tiempo) === 1
                                ? <Badge variant="success"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" />A tiempo</Badge>
                                : <Badge variant="danger"><FontAwesomeIcon icon={faTimesCircle} className="mr-1" />Fuera de plazo</Badge>
                              }
                            </td>
                            <td><small>{c.observacion || '—'}</small></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {dim3.length === 0 && (
              <p className="text-muted mb-0">
                <small>Aún no hay registros. Usa el formulario para empezar a trackear seguimientos.</small>
              </p>
            )}
          </>
        )}
      </div>

      {/* Evaluación general */}
      <div className="box p-3 mt-2">
        <h6 className="mb-3 d-flex align-items-center">
          <FontAwesomeIcon icon={faStar} className="mr-2 text-warning" />
          <strong>Evaluación general de sesiones</strong>
          <span
            className="ml-2 text-primary"
            style={{ fontSize: 12, cursor: 'pointer', fontWeight: 'normal' }}
            onClick={() => setModalEval(true)}
          >Ver más</span>
        </h6>

        <Modal show={modalEval} onHide={() => setModalEval(false)} centered size="sm">
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 16 }}>¿Qué es esta evaluación?</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontSize: 14 }}>
            <p>Es la <strong>autoevaluación del tutor</strong> sobre cómo consideró que fue cada sesión.</p>
            <p>Al completar su reporte de sesión, el tutor elige una de estas opciones:</p>
            <ul className="mb-0">
              <li><span className="text-success font-weight-bold">Excelente</span> — todo salió según lo esperado</li>
              <li><span className="text-primary font-weight-bold">Buena</span> — sesión normal sin mayores inconvenientes</li>
              <li><span className="text-warning font-weight-bold">Regular</span> — hubo algunas dificultades</li>
              <li><span className="text-danger font-weight-bold">Deficiente</span> — la sesión tuvo problemas serios</li>
            </ul>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setModalEval(false)}>Cerrar</Button>
          </Modal.Footer>
        </Modal>
        {data.evaluaciones && data.evaluaciones.length > 0 ? (
          <div className="d-flex flex-wrap" style={{ gap: 12 }}>
            {data.evaluaciones.map(ev => (
              <div key={ev.evaluacion_general} className="text-center px-3 py-2" style={{ minWidth: 100 }}>
                <Badge variant={EVAL_COLOR[ev.evaluacion_general] || 'secondary'} className="p-2 d-block mb-1" style={{ fontSize: 16 }}>
                  {ev.cantidad}
                </Badge>
                <small className="text-capitalize">{ev.evaluacion_general || 'Sin dato'}</small>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted">Sin datos aún — completa y envía reportes de sesión para ver estadísticas.</p>
        )}
      </div>

      {/* Incidencias */}
      {data.incidencias && data.incidencias.length > 0 && (
        <div className="box p-3 mt-2">
          <h6 className="mb-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-warning" />
            <strong>Tipo de incidencias reportadas</strong>
          </h6>
          <table className="table table-sm table-bordered">
            <thead className="thead-light">
              <tr>
                <th>Tipo</th>
                <th className="text-center">Cantidad</th>
              </tr>
            </thead>
            <tbody>
              {data.incidencias.map(inc => (
                <tr key={inc.tipo}>
                  <td className="text-capitalize">{inc.tipo}</td>
                  <td className="text-center">{inc.cantidad}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!data.total_sesiones && (
        <div className="box p-4 text-center text-muted mt-2">
          <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: 32, opacity: 0.3 }} className="mb-2 d-block mx-auto" />
          <p className="mb-0">Aún no hay reportes de sesión enviados. Los KPIs aparecerán cuando el tutor envíe reportes.</p>
        </div>
      )}
    </Layout>
  );
};

export default KpisTutoria;
